
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { LockKeyhole } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

interface LoginFormProps {
  email: string;
  setEmail: (email: string) => void;
  tempUserData: any;
}

export const LoginForm = ({ email, setEmail, tempUserData }: LoginFormProps) => {
  const [password, setPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  
  // Get the intended destination if available
  const from = (location.state as any)?.from?.pathname || "/";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Simple validation
    if (!email || !password) {
      toast.error("Bitte füllen Sie alle Felder aus.");
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Sign in with Supabase
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) throw error;
      
      if (!data.user) {
        throw new Error("Login fehlgeschlagen. Bitte versuchen Sie es erneut.");
      }
      
      // Check if user is approved
      const isApproved = data.user.user_metadata?.is_approved;
      if (!isApproved) {
        // Clear the session since the user isn't approved
        await supabase.auth.signOut();
        throw new Error("Ihr Konto wurde noch nicht freigeschaltet. Bitte warten Sie auf die Freischaltung durch einen Administrator.");
      }
      
      // Get username from metadata or fall back to custom logic
      const userName = data.user.user_metadata?.name || 
                        (email === "work@hoss-immo.de" ? "Hoss Immo" : 
                        tempUserData?.name || email.split('@')[0]);
      
      // Create user data to store
      const userData = {
        name: userName,
        email: email,
        id: data.user.id
      };
      
      // Check the user's role in database
      const { data: roleData } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', data.user.id)
        .single();
        
      // Determine if user is admin
      const isAdmin = roleData?.role === 'admin';
      
      // Use context login function with appropriate admin status
      login(userData, isAdmin);
      
      // Remove temporary user data
      localStorage.removeItem('tempUserData');
      
      toast.success("Anmeldung erfolgreich");
      
      // Redirect based on role
      if (isAdmin) {
        console.log("Benutzer hat Admin-Rolle, Weiterleitung zum Admin-Dashboard");
        navigate("/admin/dashboard", { replace: true });
      } else {
        console.log("Benutzer hat reguläre Rolle, Weiterleitung zum Dashboard");
        navigate("/dashboard", { replace: true });
      }
    } catch (error: any) {
      console.error("Login error:", error);
      toast.error(error.message || "Bei der Anmeldung ist ein Fehler aufgetreten.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label htmlFor="email" className="block text-white mb-2 text-center text-xl">E-Mail</label>
        <Input
          id="email"
          type="email"
          placeholder="name@firma.de"
          className="bg-[#333333] border-gray-700 text-white h-14"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={isSubmitting}
        />
      </div>
      
      <div>
        <label htmlFor="password" className="block text-white mb-2 text-center text-xl">Passwort</label>
        <Input
          id="password"
          type="password"
          className="bg-[#333333] border-gray-700 text-white h-14"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={isSubmitting}
        />
      </div>
      
      <div className="text-right">
        <a href="#" className="text-[#41c7f0] text-sm hover:underline">Passwort vergessen?</a>
      </div>
      
      <Button 
        type="submit" 
        className="w-full bg-[#41c7f0] hover:bg-[#3ab1d9] text-white h-14 rounded-md mt-6"
        disabled={isSubmitting}
      >
        {isSubmitting ? (
          <span className="flex items-center justify-center">
            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Wird verarbeitet...
          </span>
        ) : (
          <>
            <LockKeyhole className="mr-2 h-5 w-5" />
            Anmelden
          </>
        )}
      </Button>
    </form>
  );
};
